import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'app-content-dropdown-submenu-cabinet',
  templateUrl: './content-dropdown-submenu.component.html',
  styleUrls: ['./content-dropdown-submenu.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class ContentDropdownSubmenuCabinetComponent {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private destroyRef = inject(DestroyRef);

  label = input.required<string>();
  itemSelected = output<void>();

  triggerEl = viewChild.required<ElementRef<HTMLElement>>('triggerEl');
  submenuTemplate = viewChild.required<TemplateRef<unknown>>('submenuTemplate');

  private overlayRef: OverlayRef | null = null;
  isOpen = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly OPEN_DELAY = 150;
  private readonly CLOSE_DELAY = 200;

  private positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 0, offsetY: -4 },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: 0, offsetY: -4 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 0, offsetY: 4 },
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: 0, offsetY: 4 },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => this.dispose());
  }

  onTriggerEnter(): void {
    this.cancelClose();
    if (!this.isOpen) {
      this.openTimer = setTimeout(() => this.open(), this.OPEN_DELAY);
    }
  }

  onTriggerLeave(): void {
    this.cancelOpen();
    this.scheduleClose();
  }

  onPanelEnter(): void {
    this.cancelClose();
  }

  onPanelLeave(): void {
    this.scheduleClose();
  }

  open(): void {
    if (this.isOpen) return;

    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.triggerEl().nativeElement)
        .withPositions(this.positions)
        .withFlexibleDimensions(false)
        .withPush(true)
        .withViewportMargin(10);

      this.overlayRef = this.overlay.create({ positionStrategy });
    }

    const portal = new TemplatePortal(this.submenuTemplate(), this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.isOpen = true;
  }

  onPanelClick(): void {
    this.close();
    this.itemSelected.emit();
  }

  close(): void {
    this.cancelOpen();
    this.cancelClose();
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
    this.isOpen = false;
  }

  private scheduleClose(): void {
    this.closeTimer = setTimeout(() => this.close(), this.CLOSE_DELAY);
  }

  private cancelOpen(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private dispose(): void {
    this.cancelOpen();
    this.cancelClose();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
